/**
 * Micro Practices sagas
 */
import { call, put, takeLatest } from 'redux-saga/effects';
import { toast } from 'react-toastify';

import { httpRequest } from '../../services/http-service';
import { endPoints } from '../../services/endpoints';
import {
  fetchMicroPractices,
  fetchMicroPractice,
  createMicroPractice,
  updateMicroPracticeAction,
  deleteMicroPractice,
  setMicroPractices,
  setCurrentMicroPractice,
  addMicroPractice,
  updateMicroPractice,
  removeMicroPractice,
  setLoading,
  setError,
} from './microPracticesSlice';
import { presentLoader, dismissLoader } from '../../store/slices/commonSlice';

/**
 * Fetch all micro practices saga
 */
function* fetchMicroPracticesSaga(
  action: ReturnType<typeof fetchMicroPractices>
): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const params: { page?: number; limit?: number } = {
      page: 1,
      limit: 10,
    };

    if (action.payload) {
      if (action.payload.page !== undefined) params.page = action.payload.page;
      if (action.payload.limit !== undefined)
        params.limit = action.payload.limit;
      // Backend now uses shouldFilterActiveOnly(req) helper which automatically:
      // - Returns ALL records for admin panel (web) requests
      // - Filters to active only for mobile requests from non-admin users
      // The isActive query parameter is no longer used
      // activeOnly parameter is kept for backward compatibility but not sent to backend
    }

    const response = yield call(httpRequest.get, endPoints.microPractices.getAll, {
      params,
    });

    if (response?.success) {
      // Backend returns: { success: true, data: [...], pagination: {...} }
      const microPracticesData = response.data || [];
      const paginationData = response.pagination || {};

      if (Array.isArray(microPracticesData)) {
        // Pass both data and pagination to the slice
        yield put(
          setMicroPractices({
            data: microPracticesData,
            pagination: paginationData,
          })
        );
      } else {
        // If response.data is not an array, try to extract it
        const data = response.data?.data || response.data || [];
        yield put(
          setMicroPractices({
            data: Array.isArray(data) ? data : [],
            pagination: response.pagination || response.data?.pagination || {},
          })
        );
      }
    } else {
      // If response doesn't have success flag, check if it's an array directly
      if (Array.isArray(response)) {
        yield put(setMicroPractices(response));
      } else if (response?.data && Array.isArray(response.data)) {
        yield put(
          setMicroPractices({
            data: response.data,
            pagination: response.pagination || {},
          })
        );
      } else {
        toast.error('Failed to fetch micro practices');
        yield put(setError('Failed to fetch micro practices'));
      }
    }
  } catch (error: any) {
    const message = error?.message || 'Failed to fetch micro practices';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

/**
 * Fetch single micro practice saga
 */
function* fetchMicroPracticeSaga(action: ReturnType<typeof fetchMicroPractice>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));

    const response = yield call(httpRequest.get, endPoints.microPractices.getById(action.payload));

    if (response?.success) {
      yield put(setCurrentMicroPractice(response.data || response));
    } else {
      toast.error('Failed to fetch micro practice');
      yield put(setError('Failed to fetch micro practice'));
    }
  } catch (error: any) {
    const message = error?.message || 'Failed to fetch micro practice';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Create micro practice saga
 */
function* createMicroPracticeSaga(action: ReturnType<typeof createMicroPractice>): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const response = yield call(httpRequest.post, endPoints.microPractices.create, action.payload);

    if (response?.success) {
      const newMicroPractice = response.data || response;
      yield put(addMicroPractice(newMicroPractice));
    } else {
      toast.error('Failed to create micro practice');
      yield put(setError('Failed to create micro practice'));
    }
  } catch (error: any) {
    const message = error?.message || 'Failed to create micro practice';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

/**
 * Update micro practice saga
 */
function* updateMicroPracticeSaga(action: ReturnType<typeof updateMicroPracticeAction>): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const response = yield call(
      httpRequest.put,
      endPoints.microPractices.update(action.payload.id),
      action.payload.data
    );

    if (response?.success) {
      const updatedMicroPractice = { ...response.data, _id: action.payload.id };
      yield put(updateMicroPractice(updatedMicroPractice));
    } else {
      toast.error('Failed to update micro practice');
      yield put(setError('Failed to update micro practice'));
    }
  } catch (error: any) {
    const message = error?.message || 'Failed to update micro practice';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

/**
 * Delete micro practice saga
 */
function* deleteMicroPracticeSaga(action: ReturnType<typeof deleteMicroPractice>): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const response = yield call(httpRequest.delete, endPoints.microPractices.delete(action.payload));

    if (response?.success || response) {
      yield put(removeMicroPractice(action.payload));
    } else {
      toast.error('Failed to delete micro practice');
      yield put(setError('Failed to delete micro practice'));
    }
  } catch (error: any) {
    const message = error?.message || 'Failed to delete micro practice';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

export function* microPracticesSagas(): Generator<any, void, any> {
  yield takeLatest(fetchMicroPractices, fetchMicroPracticesSaga);
  yield takeLatest(fetchMicroPractice, fetchMicroPracticeSaga);
  yield takeLatest(createMicroPractice, createMicroPracticeSaga);
  yield takeLatest(updateMicroPracticeAction, updateMicroPracticeSaga);
  yield takeLatest(deleteMicroPractice, deleteMicroPracticeSaga);
}

